// Locale-aware number formatting utility

export interface SurvivalRange {
  min: number;
  max: number;
  exact: number;
}

export interface SurvivalRateRange {
  min: number;
  max: number;
  exact: number;
}

export interface BiomassRange {
  min: number;
  max: number;
  exact: number;
}

export const formatSurvivalRange = (survivalRange: SurvivalRange | null, locale: string = 'en'): string => {
  if (!survivalRange || survivalRange.exact <= 0) {
    return '-';
  }

  const { min, max } = survivalRange;
  
  // Determine if user prefers Indian numbering system
  const isIndianLocale = ['hi', 'te', 'ta', 'bn', 'gu', 'kn', 'ml', 'mr', 'pa', 'ur', 'en-IN'].includes(locale);
  
  if (isIndianLocale) {
    // Indian system: Convert to Lakhs (1 Lakh = 100,000)
    const minLakhs = min / 100000;
    const maxLakhs = max / 100000;
    
    // Format with 1 decimal place
    const minFormatted = minLakhs.toFixed(1);
    const maxFormatted = maxLakhs.toFixed(1);
    
    return `${minFormatted}-${maxFormatted}L`;
  } else {
    // International system: Convert to Thousands (1K = 1,000)
    const minThousands = min / 1000;
    const maxThousands = max / 1000;
    
    // Format with 1 decimal place
    const minFormatted = minThousands.toFixed(1);
    const maxFormatted = maxThousands.toFixed(1);
    
    return `${minFormatted}-${maxFormatted}K`;
  }
};

export const formatNumber = (value: number, locale: string = 'en'): string => {
  if (value === 0 || isNaN(value)) {
    return '-';
  }

  const isIndianLocale = ['hi', 'te', 'ta', 'bn', 'gu', 'kn', 'ml', 'mr', 'pa', 'ur', 'en-IN'].includes(locale);
  
  if (isIndianLocale && value >= 100000) {
    // Indian system: Show in Lakhs
    const lakhs = value / 100000;
    return `${lakhs.toFixed(1)}L`;
  } else if (value >= 1000) {
    // International system: Show in Thousands
    const thousands = value / 1000;
    return `${thousands.toFixed(1)}K`;
  } else {
    // Small numbers: Show as is
    return Math.round(value).toString();
  }
};

export const formatAIPredictedCount = (percentage: number, totalSeed: number, locale: string = 'en'): string => {
  if (percentage === 0 || isNaN(percentage) || totalSeed === 0) {
    return '-';
  }

  const count = (percentage / 100) * totalSeed;
  return formatNumber(count, locale);
};

export const formatSurvivalRateRange = (survivalRateRange: SurvivalRateRange | null): string => {
  if (!survivalRateRange || survivalRateRange.exact <= 0) {
    return '-';
  }

  const { min, max } = survivalRateRange;
  
  // Format with no decimal places for percentage
  const minFormatted = Math.round(min);
  const maxFormatted = Math.round(max);
  
  return `${minFormatted}-${maxFormatted}%`;
};

export const formatBiomassRange = (biomassRange: BiomassRange | null): string => {
  if (!biomassRange || biomassRange.exact <= 0) {
    return '-';
  }

  const { min, max } = biomassRange;
  
  // Format with no decimal places for biomass in kg
  const minFormatted = Math.round(min);
  const maxFormatted = Math.round(max);
  
  return `${minFormatted}-${maxFormatted}kg`;
}; 